import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import ErrorBoundary from "@/components/ErrorBoundary";
import {
  LayoutDashboard,
  ClipboardCheck,
  Store,
  Wine,
  Ruler,
  Users,
  LogOut,
  Menu,
  X,
  CheckCircle,
  Calendar,
  Gift,
  BarChart3,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  ShieldCheck,
  Search,
  Flag,
  CreditCard,
  Bell,
  FileText,
  CalendarDays,
  Settings2,
  Building2,
  Sofa,
  MapPin,
  Map,
  MessageSquare,
  Lightbulb,
  Bug,
  UserPlus,
  Sparkles,
  Webhook,
  Tags,
} from "lucide-react";
import { useState, useEffect } from "react";
import { GlobalSearch } from "@/components/GlobalSearch";
import { useNavBadges, type NavBadges } from "@/hooks/useNavBadges";

interface NavSection {
  title: string;
  icon: React.ElementType;
  items: NavItem[];
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
}

const navigationSections: NavSection[] = [
  {
    title: "Overview",
    icon: LayoutDashboard,
    items: [{ name: "Dashboard", href: "/dashboard", icon: LayoutDashboard }],
  },
  {
    title: "Content Management",
    icon: FolderOpen,
    items: [
      { name: "Bars", href: "/dashboard/bars", icon: Store },
      { name: "Bar-Vorschläge", href: "/dashboard/bar-suggestions", icon: MapPin },
      { name: "OSM Explorer", href: "/dashboard/osm-explorer", icon: Map },
      { name: "Drinks", href: "/dashboard/drinks", icon: Wine },
      { name: "Events", href: "/dashboard/events", icon: Calendar },
      { name: "Offers", href: "/dashboard/offers", icon: Gift },
      // Stories-Seite deaktiviert
      { name: "Sport-Ligen", href: "/dashboard/sport-leagues", icon: Calendar },
    ],
  },
  {
    title: "Kataloge",
    icon: FolderOpen,
    items: [
      {
        name: "Getränke-Katalog",
        href: "/dashboard/drinks-catalog",
        icon: Wine,
      },
      {
        name: "Größen-Katalog",
        href: "/dashboard/drink-sizes-catalog",
        icon: Ruler,
      },
      {
        name: "Marken-Katalog",
        href: "/dashboard/brand-catalog",
        icon: Tags,
      },
      {
        name: "Ausstattungs-Katalog",
        href: "/dashboard/amenities-catalog",
        icon: Sofa,
      },
      {
        name: "Publikums-Katalog",
        href: "/dashboard/audiences-catalog",
        icon: Users,
      },
      {
        name: "Atmosphären-Katalog",
        href: "/dashboard/atmospheres-catalog",
        icon: Sparkles,
      },
    ],
  },
  {
    title: "User Management",
    icon: Users,
    items: [{ name: "Users", href: "/dashboard/users", icon: Users }],
  },
  {
    title: "Abonnements",
    icon: CreditCard,
    items: [
      {
        name: "Trial Verwaltung",
        href: "/dashboard/subscriptions/trial",
        icon: CalendarDays,
      },
      {
        name: "Zahlungen",
        href: "/dashboard/subscriptions/payments",
        icon: CreditCard,
      },
      {
        name: "Preiskonfiguration",
        href: "/dashboard/subscriptions/pricing",
        icon: Settings2,
      },
      {
        name: "Rechnungen",
        href: "/dashboard/subscriptions/invoices",
        icon: FileText,
      },
    ],
  },
  {
    title: "Verification & Claims",
    icon: ShieldCheck,
    items: [
      { name: "Claims", href: "/dashboard/claims", icon: ClipboardCheck },
      {
        name: "Verifications",
        href: "/dashboard/verifications",
        icon: CheckCircle,
      },
    ],
  },
  {
    title: "Moderation",
    icon: AlertTriangle,
    items: [
      {
        name: "Moderation Center",
        href: "/dashboard/moderation",
        icon: AlertTriangle,
      },
      {
        name: "Feedback",
        href: "/dashboard/feedback",
        icon: MessageSquare,
      },
      {
        name: "Feature Requests",
        href: "/dashboard/feature-requests",
        icon: Lightbulb,
      },
      {
        name: "Bug Reports",
        href: "/dashboard/bug-reports",
        icon: Bug,
      },
      {
        name: "Content Reports",
        href: "/dashboard/content-reports",
        icon: Flag,
      },
    ],
  },
  {
    title: "Analytics",
    icon: BarChart3,
    items: [
      { name: "Plattform", href: "/dashboard/analytics", icon: BarChart3 },
      { name: "Brauereien", href: "/dashboard/analytics/breweries", icon: Building2 },
      { name: "Preiskarte", href: "/dashboard/analytics/bar-prices", icon: Map },
    ],
  },
  {
    title: "Developer Tools",
    icon: Bell,
    items: [
      {
        name: "n8n Webhooks",
        href: "/dashboard/developer/n8n-webhooks",
        icon: Webhook,
      },
      {
        name: "OSM API",
        href: "/dashboard/developer/osm-api",
        icon: Settings2,
      },
      {
        name: "Notifications Tester",
        href: "/dashboard/notifications-test",
        icon: Bell,
      },
      {
        name: "Notification Templates",
        href: "/dashboard/notification-templates",
        icon: FileText,
      },
      {
        name: "Admin-Rollen",
        href: "/dashboard/admin-roles",
        icon: ShieldCheck,
      },
      {
        name: "Einladungen",
        href: "/dashboard/invitations",
        icon: UserPlus,
      },
    ],
  },
  {
    title: "Kommunikation",
    icon: MessageSquare,
    items: [
      {
        name: "App-Ankündigungen",
        href: "/dashboard/announcements",
        icon: Bell,
      },
    ],
  },
  {
    title: "System",
    icon: Building2,
    items: [
      {
        name: "Unternehmenseinstellungen",
        href: "/dashboard/company-settings",
        icon: Building2,
      },
    ],
  },
];

/** Ordnet jede Nav-URL dem passenden Badge-Schlüssel zu */
const BADGE_MAP: Partial<Record<string, keyof NavBadges>> = {
  "/dashboard/claims": "claims",
  "/dashboard/verifications": "verifications",
  "/dashboard/bar-suggestions": "barSuggestions",
  "/dashboard/bug-reports": "bugReports",
  "/dashboard/feedback": "feedback",
  "/dashboard/feature-requests": "featureRequests",
  "/dashboard/content-reports": "contentReports",
};

export default function DashboardLayout() {
  const { user, signOut } = useAuth();
  const badges = useNavBadges();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "Overview",
    "Content Management",
    "Abonnements",
    "Developer Tools",
  ]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const toggleSection = (title: string) => {
    setExpandedSections((prev) =>
      prev.includes(title) ? prev.filter((s) => s !== title) : [...prev, title],
    );
  };

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] transform bg-white shadow-lg transition-transform duration-300 lg:static lg:w-72 lg:max-w-none lg:translate-x-0 xl:w-80 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between border-b border-gray-200 px-6">
            <div>
              <h1 className="text-xl font-bold text-primary-600">Kneipster</h1>
              <p className="text-xs text-gray-500">Admin Dashboard</p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
            {navigationSections.map((section) => {
              const isExpanded = expandedSections.includes(section.title);
              return (
                <div key={section.title} className="mb-2">
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(section.title)}
                    className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <section.icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{section.title}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0" />
                    )}
                  </button>

                  {/* Section Items */}
                  {isExpanded && (
                    <div className="mt-1 space-y-1 pl-2">
                      {section.items.map((item) => (
                        <NavLink
                          key={item.href}
                          to={item.href}
                          end={item.href === "/dashboard" || item.href === "/dashboard/analytics"}
                          className={({ isActive }) =>
                            `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                              isActive
                                ? "bg-primary-50 text-primary-700"
                                : "text-gray-700 hover:bg-gray-100"
                            }`
                          }
                          onClick={() => setSidebarOpen(false)}
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          <span className="flex-1 truncate">{item.name}</span>
                          {(() => {
                            const badgeKey = BADGE_MAP[item.href];
                            const count = badgeKey ? badges[badgeKey] : 0;
                            if (!count) return null;
                            return (
                              <span className="ml-auto min-w-[20px] rounded-full bg-red-500 px-1.5 py-0.5 text-center text-xs font-bold leading-none text-white">
                                {count > 99 ? "99+" : count}
                              </span>
                            );
                          })()}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="border-t border-gray-200 p-4">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                <span className="text-sm font-semibold text-primary-700">
                  {user?.email?.[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="truncate text-sm font-medium text-gray-900">
                  Admin
                </p>
                <p className="truncate text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="flex w-full items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              <LogOut className="h-4 w-4" />
              Abmelden
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 items-center border-b border-gray-200 bg-white px-4 lg:px-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-500 hover:text-gray-700 lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* Search Button */}
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors"
            >
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">Search...</span>
              <span className="hidden sm:inline text-xs text-gray-400 ml-2">
                Cmd+K
              </span>
            </button>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 flex-1 text-center lg:text-left ml-4">
            Willkommen zurück, Admin
          </h2>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* Global Search Modal */}
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
